import { redirect } from '@intigral/utils/lib/helpers/browser';
import {
    isFreeTier,
    isSubscriptionRenewalFailed,
    isShowJustUpgradeRewardCard,
    isShowMissionCompleteRenewQuest,
    isShowMissionCompleteUpgradeQuest,
    isSubscriptionRenewQuest,
    isSubscriptionUpgradeQuest,
    shouldOpenUpgradePopupForRewardCard,
    showUpgradeRewardQuest,
    showRenewRewardQuest,
} from '@intigral/utils/lib/user';
import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import useLanguage from '../../../hooks/useLanguage';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { __ } from '../../../translations';
import { getUserInfo, isKidsProfile } from '../../../utils/user';
import { GamificationRewardProperties } from '../constants/mixpanel';

const useRewardActions = () => {
    // Hooks & Context
    const language = useLanguage();
    const { track } = useContext(MixpanelContext);
    // Using React Query instead of Redux for configuration
    const { upgradedBannerModal: upgradedBannerModalConfig } = useConfigurationSections();
    const upgradedLink = upgradedBannerModalConfig?.link?.[language];

    // User Info
    const userInfo = getUserInfo();

    // Derived User Status
    const isSubsRenewalFailed = useMemo(() => isSubscriptionRenewalFailed(userInfo), [userInfo]);
    const isFreeTierUser = useMemo(() => isFreeTier(userInfo), [userInfo]);
    const isRecentUpgradeRenewalPending = useMemo(() => isShowJustUpgradeRewardCard(userInfo), [userInfo]);
    const isMissionCompleteForRenew = useMemo(
        () => isShowMissionCompleteRenewQuest(userInfo),
        [isFreeTierUser, isSubsRenewalFailed, isRecentUpgradeRenewalPending, userInfo]
    );
    const isMissionCompleteforUpgrade = useMemo(
        () => isShowMissionCompleteUpgradeQuest(userInfo),
        [isFreeTierUser, isSubsRenewalFailed, isRecentUpgradeRenewalPending, userInfo]
    );

    // Local States
    const [isBillingFailurePopupOpen, setBillingFailurePopupOpen] = useState(false);
    const [isUpgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [isRenewalDuePopupOpen, setRenewalDuePopupOpen] = useState(false);

    // Handlers
    const redirectToAccountPage = () => redirect(upgradedLink);

    const handleClickSubscriptionQuest = (quest) => {
        if (isSubscriptionQuest(quest)) {
            const questTypeId = quest?.questTypeId?.toLowerCase();
            const isRenewQuest = isSubscriptionRenewQuest(questTypeId);
            if (isSubsRenewalFailed && isRenewQuest) return setBillingFailurePopupOpen(true);
            else if (isRecentUpgradeRenewalPending && isRenewQuest) return setRenewalDuePopupOpen(true);
            else if (shouldOpenUpgradePopupForRewardCard(userInfo)) return setUpgradeModalOpen(true);
            else {
                redirectToAccountPage();
            }
        }
    };

    const handleRewardCtaClick = (quest) => {
        const event = GamificationRewardProperties[quest?.id];
        if (event) {
            track(event, { 'CTA Name': __(quest?.ctaLabel) });
        }
    };

    const upgradeHandler = (isClick: boolean) => {
        setUpgradeModalOpen(false);
        if (isClick) redirectToAccountPage();
    };

    const paymentFailedHandler = (isClick: boolean) => {
        setBillingFailurePopupOpen(false);
        if (isClick) redirectToAccountPage();
    };

    const RenewalDueNotificationHandler = () => {
        setRenewalDuePopupOpen(false);
    };

    const isSubscriptionQuest = (quest) => {
        const questTypeId = quest?.questTypeId?.toLowerCase();
        if (isSubscriptionUpgradeQuest(questTypeId) || isSubscriptionRenewQuest(questTypeId)) {
            return true;
        }
        return false;
    };

    const shouldShowSubscriptionQuest = (quest) => {
        const questTypeId = quest?.questTypeId?.toLowerCase();
        if (isSubscriptionUpgradeQuest(questTypeId)) return showUpgradeRewardQuest(userInfo);
        if (isSubscriptionRenewQuest(questTypeId)) return showRenewRewardQuest(userInfo);

        return true;
    };

    const getRedeemCTA = () => {
        if (isKidsProfile()) return null;
        return {
            linkComponent: Link,
            href: `/${language}/redeem`,
            text: __('gamification_redeem_reward_cta'),
            transformLinkProps: (props) => ({ to: props?.href }),
        };
    };

    const getSubscriptionCtaText = (quest) => {
        if (!isSubscriptionQuest(quest)) return __(quest?.ctaLabel);
        else if (isSubscriptionRenewQuest(quest?.questTypeId)) {
            if (isSubsRenewalFailed) return __('earn_reward_subscription_failed_cta');
            else if (isRecentUpgradeRenewalPending) return __('earn_reward_just_renewed_cta');
            else if (isMissionCompleteForRenew) return __('earn_reward_mission_completed_cta');
        } else if (isSubscriptionUpgradeQuest(quest?.questTypeId) && isMissionCompleteforUpgrade)
            return __('earn_reward_upgrade_mission_completed_cta');
        return __(quest?.ctaLabel);
    };

    const getQuestDescription = (quest) => {
        if (!isSubscriptionQuest(quest)) return __(quest?.description);
        else if (isSubscriptionRenewQuest(quest?.questTypeId)) {
            if (isSubsRenewalFailed) return __('earn_reward_subscription_failed_description');
            else if (isRecentUpgradeRenewalPending) return __('earn_reward_just_renew_subscription_description');
            else if (isMissionCompleteForRenew) return __('earn_reward_mission_completed_description');
        } else if (isSubscriptionUpgradeQuest(quest?.questTypeId) && isMissionCompleteforUpgrade)
            return __('earn_reward_upgrade_mission_completed_description');
        return __(quest?.description);
    };

    return {
        isBillingFailurePopupOpen,
        isUpgradeModalOpen,
        isRenewalDuePopupOpen,
        handleClickSubscriptionQuest,
        handleRewardCtaClick,
        isSubscriptionQuest,
        shouldShowMissionCompleted: (quest) => {
            const questTypeId = quest?.questTypeId?.toLowerCase();
            if (isSubscriptionRenewQuest(questTypeId)) return isMissionCompleteForRenew;
            else if (isSubscriptionUpgradeQuest(questTypeId)) return isMissionCompleteforUpgrade;
            return false;
        },
        shouldShowSubscriptionQuest,
        getRedeemCTA,
        getSubscriptionCtaText,
        getQuestDescription,
        isSubsRenewalFailed,
        upgradeHandler,
        paymentFailedHandler,
        RenewalDueNotificationHandler,
    };
};

export default useRewardActions;
